import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Navigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { LifeBuoy, Loader2, Search, Filter, ShieldCheck, ArrowRight, Download } from "lucide-react";
import { toast } from "sonner";
import { STATUS_META, categoryLabel, formatWhen, RESOLUTIONS } from "@/lib/complaints";

const StatTile = ({ label, value, accent }) => (
  <Card className="p-4 bg-white border-black/10">
    <div className="label-overline text-muted-foreground">{label}</div>
    <div className={`font-display text-3xl mt-1 ${accent || ""}`}>{value}</div>
  </Card>
);

const AdminComplaints = () => {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const focusId = params.get("id");

  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [status, setStatus] = useState("");
  const [category, setCategory] = useState("");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [resolveTarget, setResolveTarget] = useState(null);
  const [resolution, setResolution] = useState("no_action");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportSince, setExportSince] = useState("");
  const [exportUntil, setExportUntil] = useState("");

  const exportCsv = async () => {
    setExporting(true);
    try {
      const params = {};
      if (exportSince) params.since = exportSince;
      if (exportUntil) params.until = exportUntil;
      const res = await api.get("/admin/export/complaints.csv", { params, responseType: "blob" });
      const blob = new Blob([res.data], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const today = new Date().toISOString().slice(0, 10).replace(/-/g, "");
      const rangeBit = exportSince && exportUntil
        ? `-${exportSince}_to_${exportUntil}`
        : exportSince ? `-from-${exportSince}`
        : exportUntil ? `-until-${exportUntil}` : "";
      a.download = `afrimarket-complaints${rangeBit}-${today}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Complaints CSV downloaded");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to export complaints");
    } finally {
      setExporting(false);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/complaints", {
        params: { status: status || undefined, category: category || undefined, q: q || undefined },
      });
      setItems(data.complaints || []);
      setCounts(data.counts || {});
    } catch (e) { toast.error("Could not load"); }
    finally { setLoading(false); }
  };

  useEffect(() => { if (user?.role === "admin") load(); /* eslint-disable-next-line */ }, [status, category, user]);

  useEffect(() => {
    if (focusId && items.length) {
      const c = items.find((x) => x.complaint_id === focusId);
      if (c) setResolveTarget(c);
    }
  }, [focusId, items]);

  const resolve = async () => {
    if (!resolveTarget) return;
    setBusy(true);
    try {
      await api.post(`/admin/complaints/${resolveTarget.complaint_id}/resolve`, { resolution, notes });
      toast.success("Case resolved");
      setResolveTarget(null);
      setNotes("");
      setResolution("no_action");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not resolve");
    } finally { setBusy(false); }
  };

  if (!user) return null;
  if (user.role !== "admin") return <Navigate to="/" replace />;

  return (
    <div className="container-narrow py-10 max-w-6xl">
      <div className="flex items-end justify-between flex-wrap gap-3 mb-6">
        <div>
          <div className="label-overline text-[#C05A3C] mb-2">Admin · Resolution centre</div>
          <h1 className="font-display text-4xl">Cases queue</h1>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <label className="flex items-center gap-1.5 text-xs text-muted-foreground">From
            <Input type="date" value={exportSince} onChange={(e) => setExportSince(e.target.value)} className="h-9 w-40" data-testid="export-since"/>
          </label>
          <label className="flex items-center gap-1.5 text-xs text-muted-foreground">To
            <Input type="date" value={exportUntil} onChange={(e) => setExportUntil(e.target.value)} className="h-9 w-40" data-testid="export-until"/>
          </label>
          <Button variant="outline" size="sm" onClick={exportCsv} disabled={exporting} data-testid="export-complaints-csv" className="border-[#4A5D23] text-[#4A5D23]">
            {exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin"/> : <Download className="w-4 h-4 mr-2"/>}
            Export CSV
          </Button>
          <Link to="/admin" className="text-sm text-muted-foreground underline hover:text-[#C05A3C]">← Back to admin panel</Link>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3 mb-6">
        <StatTile label="Open" value={counts.open || 0} accent="text-[#D9923B]" />
        <StatTile label="Responded" value={counts.responded || 0} accent="text-[#233348]" />
        <StatTile label="Escalated" value={counts.escalated || 0} accent="text-[#C05A3C]" />
        <StatTile label="Resolved" value={counts.resolved || 0} accent="text-[#4A5D23]" />
        <StatTile label="Rejected" value={counts.rejected || 0} />
        <StatTile label="Withdrawn" value={counts.withdrawn || 0} />
      </div>

      <Card className="p-4 mb-4 bg-white border-black/10 flex flex-wrap items-center gap-3" data-testid="admin-complaints-filters">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} placeholder="Search subject, email…" className="pl-9" data-testid="admin-complaints-search" />
        </div>
        <Select value={status || "all"} onValueChange={(v) => setStatus(v === "all" ? "" : v)}>
          <SelectTrigger className="w-[160px]" data-testid="admin-complaints-status"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {Object.entries(STATUS_META).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button variant="outline" onClick={load}><Filter className="w-4 h-4 mr-1" /> Apply</Button>
      </Card>

      {loading ? (
        <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
      ) : items.length === 0 ? (
        <Card className="p-10 text-center border-dashed bg-[#FBF9F4]"><LifeBuoy className="w-8 h-8 mx-auto text-muted-foreground mb-3" /><div className="text-sm text-muted-foreground">No cases match these filters.</div></Card>
      ) : (
        <div className="space-y-2">
          {items.map((c) => {
            const meta = STATUS_META[c.status] || STATUS_META.open;
            return (
              <Card key={c.complaint_id} className="p-4 bg-white border-black/10 hover:border-[#C05A3C]/40 transition-colors" data-testid={`admin-complaint-${c.complaint_id}`}>
                <div className="flex items-start gap-3 flex-wrap">
                  <div className="flex-1 min-w-[260px]">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge className={`${meta.color} text-[10px]`}>{meta.label}</Badge>
                      <span className="text-xs text-muted-foreground">{categoryLabel(c.category)}</span>
                      {c.payout_held && <Badge className="bg-[#1A2024] text-[#D9923B] text-[10px]">PAYOUT HELD</Badge>}
                    </div>
                    <div className="font-semibold mt-1">{c.subject}</div>
                    <div className="text-xs text-muted-foreground mt-1">
                      {c.reporter_email} → {c.respondent_email || "—"} · {formatWhen(c.updated_at)}
                    </div>
                  </div>
                  <div className="flex gap-2 ml-auto">
                    <Link to={`/complaints/${c.complaint_id}`}>
                      <Button variant="outline" size="sm">View thread <ArrowRight className="w-3 h-3 ml-1" /></Button>
                    </Link>
                    {!["resolved", "rejected", "withdrawn"].includes(c.status) && (
                      <Button size="sm" onClick={() => setResolveTarget(c)} className="bg-[#C05A3C] hover:bg-[#D46B4E] text-white" data-testid={`resolve-${c.complaint_id}`}>
                        <ShieldCheck className="w-3.5 h-3.5 mr-1" /> Resolve
                      </Button>
                    )}
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      <Dialog open={!!resolveTarget} onOpenChange={(o) => !o && setResolveTarget(null)}>
        <DialogContent data-testid="resolve-dialog">
          <DialogHeader>
            <DialogTitle>Resolve case</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div className="bg-[#F3EFE9] rounded-md p-3">
              <div className="text-xs text-muted-foreground">Subject</div>
              <div className="font-semibold">{resolveTarget?.subject}</div>
              <div className="text-xs mt-1 text-muted-foreground">{categoryLabel(resolveTarget?.category)}</div>
            </div>
            <div>
              <label className="label-overline">Resolution</label>
              <Select value={resolution} onValueChange={setResolution}>
                <SelectTrigger className="mt-1" data-testid="resolution-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {RESOLUTIONS.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <div className="text-xs text-muted-foreground mt-1">
                {resolution === "listing_removed" && "The linked listing will be removed."}
                {resolution === "user_suspended" && "The respondent will be suspended from the platform."}
                {resolution === "refund_issued" && "Payout stays on hold so you can process a refund off-platform."}
                {resolution === "refund_denied" && "Any held payout will be released to the seller."}
                {resolution === "no_action" && "Case dismissed. Any held payout will be released."}
              </div>
            </div>
            <div>
              <label className="label-overline">Notes for parties</label>
              <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={4} placeholder="Explain the decision (visible to both parties via email)…" data-testid="resolution-notes" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setResolveTarget(null)}>Cancel</Button>
            <Button disabled={busy} onClick={resolve} className="bg-[#C05A3C] hover:bg-[#D46B4E] text-white" data-testid="confirm-resolve">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Resolve case"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminComplaints;
